import { useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { DropdownMenu, MenuItem, MenuLabel } from '@/shared/ui/dropdown-menu';
import { Icon, type PhxIconName } from '@/shared/ui/icon';
import { FIELD_FOCUS_RING } from '@/shared/ui/fieldStyles';

export interface MenuSelectOption {
  value: string;
  label: string;
  /** Sekundární text vpravo v rozbalené položce (např. souřadnice). */
  hint?: ReactNode;
}

export type MenuSelectSize = 'sm' | 'md';

export interface MenuSelectProps {
  /** Vybraná hodnota; `null` = nevybráno (zobrazí se `placeholder`). */
  value: string | null;
  onChange: (value: string | null) => void;
  options: MenuSelectOption[];
  /** Text tlačítka, když není nic vybráno (účel pole) — např. „Nástupní místo". */
  placeholder?: string;
  /**
   * Popisek prázdné volby v seznamu (vyčištění výběru) — např. „Bez určení". Bez něj se použije
   * {@link placeholder}. Položka se nabízí jen je-li `emptyOptionLabel` nebo `placeholder` zadané.
   */
  emptyOptionLabel?: string;
  /** Ikona uvnitř spouštěcího tlačítka (vlevo). */
  icon?: PhxIconName;
  /** Nadpis nad seznamem v rozbaleném menu. */
  menuLabel?: ReactNode;
  size?: MenuSelectSize;
  ariaLabel?: string;
  className?: string;
}

const TRIGGER_BASE =
  'phx-menu-select inline-flex w-full items-center justify-between gap-phx-2 ' +
  'rounded-phx border border-phx-border-2 bg-phx-surface text-phx-text cursor-pointer text-left ' +
  'transition-[border-color,box-shadow] hover:border-phx-border-3 ' +
  'data-[open]:border-phx-primary data-[open]:shadow-phx-focus';

const TRIGGER_SIZE: Record<MenuSelectSize, string> = {
  sm: 'h-phx-control-sm text-phx-xs px-phx-3',
  md: 'h-phx-control text-phx-md px-phx-4',
};

/**
 * Lehký jednovýběrový dropdown ve stylu Phoenix selectu — pěkněji stylované tlačítko (volitelná ikona)
 * + popover seznam (postavený na sdíleném {@link DropdownMenu}; portál, klik mimo, Esc). Umí nadpis
 * seznamu ({@link MenuSelectProps.menuLabel}), zaškrtnutí vybrané položky a sekundární text vpravo
 * ({@link MenuSelectOption.hint}). Vhodný pro krátké číselníky.
 */
export function MenuSelect({
  value,
  onChange,
  options,
  placeholder,
  emptyOptionLabel,
  icon,
  menuLabel,
  size = 'sm',
  ariaLabel,
  className,
}: MenuSelectProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  const labelText = selected?.label ?? placeholder ?? '';
  const clearLabel = emptyOptionLabel ?? placeholder;

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      backdrop
      className="max-h-70 overflow-auto"
      trigger={
        <button
          type="button"
          aria-label={ariaLabel}
          data-open={open ? '' : undefined}
          className={cn(TRIGGER_BASE, FIELD_FOCUS_RING, TRIGGER_SIZE[size], className)}
        >
          <span className="gap-phx-2 flex min-w-0 items-center leading-none">
            {icon ? <Icon name={icon} size={13} className="text-phx-text-muted shrink-0" /> : null}
            <span
              className={cn('min-w-0 truncate leading-none', selected ? '' : 'text-phx-text-muted')}
            >
              {labelText}
            </span>
          </span>
          <Icon name="chevdown" size={12} className="text-phx-text-muted shrink-0" />
        </button>
      }
    >
      {menuLabel ? <MenuLabel>{menuLabel}</MenuLabel> : null}
      {clearLabel ? (
        <MenuItem
          active={value === null}
          iconNode={value === null ? <Icon name="check" size={13} /> : <span />}
          onClick={() => select(null)}
        >
          {clearLabel}
        </MenuItem>
      ) : null}
      {options.map((option) => (
        <MenuItem
          key={option.value}
          active={option.value === value}
          iconNode={option.value === value ? <Icon name="check" size={13} /> : <span />}
          hint={option.hint}
          onClick={() => select(option.value)}
        >
          {option.label}
        </MenuItem>
      ))}
    </DropdownMenu>
  );
}
